import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { FiCalendar, FiMapPin, FiClock, FiUser } from "react-icons/fi";

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "events", id));
        if (snap.exists()) setEvent({ id: snap.id, ...snap.data() });
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-xl text-gray-400 mb-4">Event not found.</p>
        <Link to="/events" className="text-[#e84fa5] hover:underline">← Back to Events</Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* HERO BANNER */}
      {event.image && (
        <div className="w-full h-[300px] md:h-[400px] overflow-hidden">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-12 py-10">
        {/* Breadcrumb */}
        <div className="text-[12px] text-gray-400 mb-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#e84fa5] transition">Home</Link>
          <span>/</span>
          <Link to="/events" className="hover:text-[#e84fa5] transition">Events</Link>
          <span>/</span>
          <span className="text-gray-700">{event.title}</span>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-extrabold text-black leading-tight mb-6">{event.title}</h1>

        {/* Event Meta */}
        <div className="flex flex-wrap gap-5 mb-8 text-[13px] text-gray-600">
          {event.eventDate && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiCalendar className="text-[#e84fa5] shrink-0" size={16} />
              <span>{new Date(event.eventDate).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
          )}
          {event.time && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiClock className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.time}</span>
            </div>
          )}
          {event.venue && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiMapPin className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.venue}</span>
            </div>
          )}
          {event.organizer && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiUser className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.organizer}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <div className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-line mb-8">
            {event.description}
          </div>
        )}

        {/* Registration Button */}
        {event.registrationLink && (
          <a
            href={event.registrationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-black text-white font-bold uppercase text-[13px] rounded-lg hover:bg-gray-800 transition"
          >
            Register Now
          </a>
        )}

        {/* Back link */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <Link to="/events" className="text-[13px] text-[#e84fa5] font-semibold hover:underline">
            ← Back to All Events
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
